import { __awaiter } from "tslib";
import { DivWithSpinner, IntegratedDialog } from "@tedivo/tedivo-ui";
import { FederationProvidersEnum, isMfaEnabledForCognitoUser, systemRights, } from "@tedivo/tvd-api-models";
import { TedivoForm, translateTedivoForm, } from "@tedivo/tedivo-form";
import { SmartTable, } from "@tedivo/tedivo-smart-table";
import createNotification, { createErrorNotification, } from "../../../app/notifications/createNotification";
import { getRemediateOptions, getUserStatusClassName, handleRemediateAction, } from "./userRemediationActions";
import { getTranslation, i18nReactive } from "../../../app/i18/i18tn";
import { EditDrawer } from "../../common/EditDrawer";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import { IntegratedDialogError } from "@tedivo/tedivo-ui";
import Services from "../../../app/services";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import SlDialog from "@shoelace-style/shoelace/dist/components/dialog/dialog";
import UserTypeEnum from "../../../app/enums/UserTypeEnum";
import { createTableUserStorageInstance } from "../../../app/preferences/createTableUserStorageInstance";
import { formatDateInCurrentTimezone } from "@tedivo/tedivo-pure-helpers";
import globalStore from "../../../app/stores/globalStore";
import goSquared from "../../../app/tracking/goSquared";
import { mergeUsersWithCurrentOrgData } from "../admin/organization-users.component";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import securityModule from "../../../app/security/SecurityModule";
import { setAppTitle } from "../../../app/setAppTitle";
import topMessageElement from "../../layout/top-tools/getTopMessageElement";
import { z } from "zod";
export class TVDMyOrganizationComponent extends HTMLElement {
    constructor() {
        super();
        this.myOrgFederations = [];
        this.displayOrganizationUsers = () => {
            globalStore.touchLastUserInteraction();
            const orgId = securityModule.currentOrganizationId;
            if (!orgId)
                return;
            const wrapper = this.wrapper;
            const box = this.mainBox;
            const actionBox = document.createElement("div");
            actionBox.className = "table-action-box";
            removeChildren(box);
            if (!securityModule.planIsReadOnly &&
                securityModule.userHasPermission(systemRights.ORG.CanCreateMoreThanOneUser) &&
                securityModule.userHasPermission(systemRights.USER.CreateUser)) {
                const actionAddUserButton = document.createElement("sl-button");
                actionAddUserButton.innerHTML = getTranslation("general:users.adminCreate");
                actionAddUserButton.variant = "primary";
                actionAddUserButton.addEventListener("click", () => {
                    this.openOrganizationUserEditModal();
                }, false);
                actionBox.appendChild(actionAddUserButton);
            }
            if (!securityModule.planIsReadOnly &&
                securityModule.userHasPermission(systemRights.USER.CanChangeOrganizationName)) {
                const actionEditOrgNameButton = document.createElement("sl-button");
                actionEditOrgNameButton.innerHTML = getTranslation("general:organizations.changeOrganizationDetails");
                actionEditOrgNameButton.variant = "primary";
                actionEditOrgNameButton.addEventListener("click", () => __awaiter(this, void 0, void 0, function* () {
                    actionEditOrgNameButton.loading = true;
                    yield this.openOrganizationEditModal({
                        canChangeFederations: !!process.env.NX_PUBLIC_ENABLE_FEDERATIONS &&
                            securityModule.currentOrganizationPlanId !== "FREE" &&
                            securityModule.userHasPermission(systemRights.USER.UserCanChangeFederations),
                    });
                    actionEditOrgNameButton.loading = false;
                }), false);
                actionBox.appendChild(actionEditOrgNameButton);
            }
            wrapper.setLoading(true);
            Services.organizations
                .getSingleOrganizationAndUsers(orgId)
                .then((resp) => {
                if (resp.data) {
                    const userOptionsStorage = createTableUserStorageInstance("myOrganizationUsersList");
                    box.appendChild(createOrgUsersTable({
                        data: resp.data,
                        userOptionsStorage,
                        actionBox,
                        onEditOrganizationUser: (o) => {
                            this.openOrganizationUserEditModal(Object.assign({}, o));
                        },
                        onDeleteOrganizationUser: this.deleteOrganizationUser,
                        userType: securityModule.userRole,
                        isReadOnly: securityModule.planIsReadOnly,
                        onRefresh: () => this.displayOrganizationUsers(),
                        onReInvite: (u) => {
                            this.openOrganizationUserEditModal(Object.assign(Object.assign({}, u), { sub: undefined }));
                        },
                    }));
                }
            })
                .catch((e) => {
                this.showError(e);
            })
                .finally(() => {
                wrapper.setLoading(false);
            });
        };
        this.showError = (e, data) => {
            this.dialogError.show(getTranslation(`errors:errorHasOcurred`), getTranslation(typeof e === "string" ? e : e.translationKey, data));
        };
        this.openOrganizationUserEditModal = (prevData) => __awaiter(this, void 0, void 0, function* () {
            const orgId = securityModule.currentOrganizationId;
            if (!orgId)
                return;
            const sub = prevData === null || prevData === void 0 ? void 0 : prevData.sub;
            let editForm = null;
            const drawer = this.editDrawer.getEditDrawer({
                title: getTranslation(prevData ? "general:users.adminEdit" : "general:users.adminCreate", {
                    organizationName: securityModule.currentOrganizationName,
                }),
                showUnits: false,
                readOnlyMode: false,
                onSave: () => __awaiter(this, void 0, void 0, function* () {
                    this.editDrawer.disabled = true;
                    const valid = (yield (editForm === null || editForm === void 0 ? void 0 : editForm.submitFunction())) || false;
                    this.editDrawer.disabled = false;
                    if (valid) {
                        this.displayOrganizationUsers();
                    }
                    return valid;
                }),
            });
            const holder = document.createElement("div-spinner-element");
            holder.setLoading(true);
            drawer.appendChild(holder);
            drawer.show();
            if (sub && prevData) {
                const u = yield Services.accounts.getUserDetailsByAdmin(orgId, prevData.mainIdentityId);
                // If the request failed, `mfa` stays false, which leaves the checkbox
                // unchecked *and* disabled: we never offer to disable MFA we couldn't read.
                prevData.mfa = isMfaEnabledForCognitoUser(u.data);
            }
            editForm = createUserEdit({
                orgId,
                prevData,
                showError: this.showError,
                allowedDomains: securityModule.orgAllowedDomains,
                myOrgFederations: this.myOrgFederations,
                submitButton: this.editDrawer.submitButton,
            });
            if (editForm) {
                holder.appendChild(editForm.node);
            }
            holder.setLoading(false);
        });
        this.openOrganizationEditModal = (_a) => __awaiter(this, [_a], void 0, function* ({ canChangeFederations, }) {
            let myOrgFederationsFn = undefined;
            if (canChangeFederations) {
                myOrgFederationsFn = () => __awaiter(this, void 0, void 0, function* () {
                    this.myOrgFederations = (yield Services.accounts.getOrgFederations()).data || [];
                    return this.myOrgFederations;
                });
            }
            const editForm = createOrganizationEditModal(this.editDrawer.submitButton, this.showError, canChangeFederations, myOrgFederationsFn);
            if (editForm) {
                const drawer = this.editDrawer.getEditDrawer({
                    title: getTranslation("general:organizations.changeOrganizationDetails"),
                    showUnits: false,
                    readOnlyMode: false,
                    onSave: () => __awaiter(this, void 0, void 0, function* () {
                        this.editDrawer.disabled = true;
                        const valid = yield editForm.submitFunction();
                        this.editDrawer.disabled = false;
                        if (valid) {
                            this.displayOrganizationUsers();
                            myOrgFederationsFn === null || myOrgFederationsFn === void 0 ? void 0 : myOrgFederationsFn();
                        }
                        return valid;
                    }),
                });
                drawer.appendChild(editForm.node);
                drawer.show();
            }
        });
        this.deleteOrganizationUser = (orgId, dta) => {
            const dialog = this.deleteDialog;
            removeChildren(dialog);
            dialog.label = getTranslation("general:users.delete");
            const htmlContent = document.createElement("div");
            htmlContent.innerHTML = `${getTranslation("general:users.delete")}<br /><br /><strong>${dta.name} ${dta.familyName}</strong><br />${dta.email}`;
            const deleteBtn = document.createElement("sl-button");
            deleteBtn.slot = "footer";
            deleteBtn.variant = "danger";
            deleteBtn.autofocus = true;
            deleteBtn.tabIndex = 0;
            deleteBtn.innerHTML = getTranslation("general:common.delete");
            deleteBtn.addEventListener("click", () => __awaiter(this, void 0, void 0, function* () {
                deleteBtn.loading = true;
                deleteBtn.disabled = true;
                const id = dta.userStatusType !== "EXTERNAL_PROVIDER" ? dta.sub : encodeURI(dta.username);
                yield Services.organizations.adminDeleteOrganizationUser(orgId, dta.sub, id);
                deleteBtn.loading = false;
                dialog.hide();
                this.displayOrganizationUsers();
            }), false);
            dialog.appendChild(htmlContent);
            dialog.appendChild(deleteBtn);
            this.appendChild(dialog);
            dialog.show();
        };
        this.i18nCR = new I18nComponentRegisterer(i18nReactive);
        this.dialogError = new IntegratedDialogError(this, false, getTranslation("general:common.close"));
        this.wrapper = new DivWithSpinner();
        this.mainBox = document.createElement("div");
        this.mainBox.className = "oss-card";
        this.deleteDialog = document.createElement("sl-dialog");
        this.editDrawer = new EditDrawer();
        setAppTitle(getTranslation("menu:organization"));
    }
    connectedCallback() {
        goSquared.trackPage("My Organization");
        goSquared.addEvent("My Organization - Show page");
        const wrapper = this.wrapper, h1 = document.createElement("h1");
        wrapper.appendChild(h1);
        wrapper.appendChild(this.mainBox);
        this.appendChild(this.editDrawer.drawer);
        this.appendChild(wrapper);
        Services.accounts.getOrgFederations().then((resp) => {
            this.myOrgFederations = resp.data || [];
        });
        this.displayOrganizationUsers();
        this.i18nCR.addConsumer(topMessageElement.element, "menu:organization", "innerHTML");
        this.i18nCR.addConsumer(h1, "general:users.users", "innerHTML");
    }
    disconnectedCallback() {
        this.i18nCR.disconnect();
    }
}
TVDMyOrganizationComponent.observedAttributes = [];
customElements.define("tvd-my-organization-component", TVDMyOrganizationComponent);
function createOrgUsersTable({ data, userOptionsStorage, actionBox, onEditOrganizationUser, onDeleteOrganizationUser, userType, isReadOnly, onRefresh, onReInvite, }) {
    var _a, _b;
    const createSortIcon = (asc) => {
        const icon = document.createElement("sl-icon");
        icon.name = asc ? "sort-down-alt" : "sort-up-alt";
        return icon;
    };
    const organizationId = ((_a = data.organization) === null || _a === void 0 ? void 0 : _a.organizationId) || "";
    const users = mergeUsersWithCurrentOrgData(organizationId, data.users || []);
    const fields = [
        {
            name: "name",
            label: getTranslation("general:common.name"),
        },
        {
            name: "familyName",
            label: getTranslation("general:users.familyName"),
        },
        {
            name: "email",
            label: getTranslation("general:common.email"),
        },
        {
            name: "organizationUserEnabled",
            label: getTranslation("general:users.orgAndSystemEnabled"),
            className: (u) => ((u === null || u === void 0 ? void 0 : u.cognitoEnabled) === false ? "centered state-warning" : "centered"),
            mapper: (u) => `${getTranslation(`general:common.${u.organizationUserEnabled === "1" ? "yes" : "no"}`)} / ${getTranslation(`general:common.${u.cognitoEnabled ? "yes" : "no"}`)}`,
            notSortable: true,
        },
        {
            name: "userStatusType",
            label: getTranslation("general:common.status"),
            mapper: (u) => {
                if (u.isFederated !== "1") {
                    return getTranslation(`enums:UserStatusType.${u.userStatusType}`);
                }
                return getTranslation(`enums:UserStatusType.EXTERNAL_PROVIDER`);
            },
            sortValueFunction: (u) => (u.isFederated === "1" ? "EXTERNAL_PROVIDER" : u.userStatusType),
            className: (u) => getUserStatusClassName(u),
        },
        {
            name: "organizationUserRole",
            label: getTranslation("general:common.type"),
            mapper: (u) => getTranslation(`enums:UserTypeEnum.${u.organizationUserRole}`),
        },
        {
            name: "userCreateDate",
            label: getTranslation("general:common.createdAt"),
            valueFunction: (dta) => dta.userCreateDate ? formatDateInCurrentTimezone(dta.userCreateDate) : "",
            className: "centered",
        },
        {
            name: "userLastModifiedDate",
            label: getTranslation("general:common.modifiedAt"),
            valueFunction: (dta) => dta.userLastModifiedDate ? formatDateInCurrentTimezone(dta.userLastModifiedDate) : "",
            className: "centered",
        },
    ];
    if (userType !== "USER" && isReadOnly === false) {
        if (securityModule.userIsTedivoAdmin ||
            securityModule.userHasPermission(systemRights.USER.UserCanRemediateOrganizationUser)) {
            fields.push({
                name: "sub",
                label: getTranslation("general:users.cognitoActions"),
                type: "actions",
                icon: "three-dots-vertical",
                iconVariant: "primary",
                notSortable: true,
                fixedWidth: "60px",
                smallPadding: true,
                options: (dta) => getRemediateOptions(dta, false),
                onClick: (dta, _name, _table, cmd) => {
                    if (!cmd)
                        return;
                    handleRemediateAction({
                        orgId: organizationId,
                        dta,
                        action: cmd,
                        onReInvite,
                        onRefresh,
                    });
                },
            });
        }
        fields.push({
            name: "sub",
            label: getTranslation("general:common.edit"),
            type: "icon",
            icon: (dta) => userType === "SUPERADMIN" || (userType === "ADMIN" && dta.planId !== "SUPERADMIN")
                ? "pencil"
                : "",
            notSortable: true,
            onClick: (dta) => {
                onEditOrganizationUser(dta);
            },
        }, {
            name: "sub",
            label: getTranslation("general:common.delete"),
            type: "icon",
            icon: (dta) => `USER#${securityModule.userSub}` !== dta.sub
                ? userType === "SUPERADMIN" || (userType === "ADMIN" && dta.planId !== "SUPERADMIN")
                    ? "trash"
                    : ""
                : "",
            iconVariant: "danger",
            notSortable: true,
            onClick: (dta) => {
                onDeleteOrganizationUser(organizationId, dta);
            },
        });
    }
    const tableUsers = new SmartTable().initialize({
        className: "tvd-table tvd-table-smart-table",
        data: users || [],
        defaultSortBy: "name",
        title: ((_b = data.organization) === null || _b === void 0 ? void 0 : _b.name) || "",
        fields,
        settings: {
            sortEnabled: true,
            sortVisible: true,
            userOptionsStorage,
            filters: {
                textFieldEnabled: true,
                textFieldFilterFields: ["name", "familyName", "email"],
                initialTextFieldFilter: userOptionsStorage.retrieveFilterText(),
            },
            labelSortBy: getTranslation("general:common.sortBy"),
            labelSearch: getTranslation("general:common.filterBy"),
            createSortIconFn: createSortIcon,
            actionBox,
        },
        pkFunction: (dta) => dta.sub,
    });
    return tableUsers;
}
function createUserEdit({ submitButton, orgId, prevData, showError, allowedDomains, myOrgFederations, }) {
    var _a;
    const holder = document.createElement("div");
    const formValidator = z
        .object({
        email: z
            .string()
            .email()
            .transform((v) => v === null || v === void 0 ? void 0 : v.toLowerCase()),
        name: z.string().min(1),
        familyName: z.string().min(1),
        organizationUserEnabled: z.boolean(),
        organizationUserRole: z.nativeEnum(UserTypeEnum),
        isFederated: z.boolean().optional(),
        mfa: z.boolean().optional(),
    })
        .refine((data) => {
        if (data.email) {
            const domain = (data.email.split("@")[1] || "").toLowerCase().trim();
            if (allowedDomains.indexOf(domain) < 0)
                return false;
            return true;
        }
        else
            return false;
    }, {
        path: ["email"],
        message: getTranslation("errors:domainNotAllowed"),
    });
    const domain = (_a = prevData === null || prevData === void 0 ? void 0 : prevData.email) === null || _a === void 0 ? void 0 : _a.split("@")[1];
    const hasMfa = !!(prevData === null || prevData === void 0 ? void 0 : prevData.mfa);
    const sameUser = (prevData === null || prevData === void 0 ? void 0 : prevData.sub) === securityModule.userSub;
    const userIsSuperAdmin = (prevData === null || prevData === void 0 ? void 0 : prevData.organizationUserRole) === "SUPERADMIN";
    const userIsFederated = !!prevData && prevData.isFederated === "1";
    const domainFederation = myOrgFederations.find((f) => f.domain === domain);
    const domainIsFederated = !!(domainFederation === null || domainFederation === void 0 ? void 0 : domainFederation.enabled);
    const federationDisabledReason = !domainIsFederated
        ? "general:organizations.federations.federationDisabledNotAllowed"
        : userIsSuperAdmin
            ? "general:organizations.federations.federationDisabledSuperAdmin"
            : "general:organizations.federations.federationEnabledProvider";
    const federatedInfoNode = document.createElement("div");
    if (userIsFederated) {
        federatedInfoNode.innerHTML = getTranslation("general:organizations.federations.disableUserEditsForFederatedUsers");
    }
    const formFields = [
        [
            {
                name: "name",
                label: "general:common.name",
                type: "textBox",
                initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.name) || "",
            },
            {
                name: "familyName",
                label: "general:users.familyName",
                type: "textBox",
                initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.familyName) || "",
            },
        ],
        [
            {
                name: "email",
                label: "general:common.email",
                type: "textBox",
                disabled: userIsFederated,
                initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.email) || "",
                helpText: getTranslation("general:users.domainsHelpText", {
                    domains: allowedDomains.join(", ") || "-",
                }),
            },
            {
                name: "organizationUserEnabled",
                label: "general:common.enabled",
                type: "checkbox",
                helpText: sameUser
                    ? "general:organizations.userCannotBeDeactivated"
                    : "general:organizations.disableUserHelp",
                initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.organizationUserEnabled) === undefined
                    ? true
                    : (prevData === null || prevData === void 0 ? void 0 : prevData.organizationUserEnabled) === "1",
                padStart: true,
                disabled: sameUser,
            },
        ],
        [
            {
                name: "isFederated",
                label: "general:organizations.federations.federationEnabled",
                type: "checkbox",
                disabled: !securityModule.userHasPermission(systemRights.USER.UserCanChangeFederatedUsers) ||
                    userIsSuperAdmin ||
                    !domainIsFederated,
                initialValue: domainIsFederated ? userIsFederated : false,
                helpText: getTranslation(federationDisabledReason, {
                    provider: (domainFederation === null || domainFederation === void 0 ? void 0 : domainFederation.provider) || "",
                }),
            },
            // Only when editing: on create/invite there is no MFA state to show, and
            // submitting the (disabled) unchecked box would read as "disable MFA".
            prevData &&
                securityModule.userHasPermission(systemRights.USER.CanDisableOthersMfa)
                ? {
                    name: "mfa",
                    label: "auth:mfa.activeMfa",
                    helpText: sameUser
                        ? "auth:mfa.mfaCanOnlyBeModifiedYourselfInProfile"
                        : hasMfa
                            ? "auth:mfa.mfaCanDisableForUser"
                            : "auth:mfa.mfaCanOnlyBeActivatedBySameUser",
                    disabled: !hasMfa || sameUser || userIsFederated,
                    type: "checkbox",
                    initialValue: hasMfa,
                }
                : undefined,
        ],
        createFieldOptionsForUserTypeEdit(securityModule.userRole || "USER", userIsFederated),
        {
            type: "node",
            node: federatedInfoNode,
            name: "",
        },
    ];
    const tedivoForm = new TedivoForm({
        fields: formFields,
        onSubmit: () => undefined,
        formValidator,
        submitButton: submitButton,
        formProps: { autoFocusOnFirstInput: true },
        hiddenData: prevData,
    });
    translateTedivoForm({
        tedivoForm,
        getTranslation: getTranslation,
    });
    holder.appendChild(tedivoForm.form);
    return {
        node: holder,
        tedivoForm,
        submitFunction: submitPassedToEditDrawer,
    };
    function submitPassedToEditDrawer() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const validResult = tedivoForm.doSubmitForm();
            if (validResult.success) {
                const values = validResult.data;
                if (prevData === null || prevData === void 0 ? void 0 : prevData.sub) {
                    const data = Object.assign(Object.assign(Object.assign({}, prevData), values), { organizationUserEnabled: values.organizationUserEnabled ? "1" : "0", organizationUserRole: values.organizationUserRole });
                    const resp = yield Services.organizations.adminUpdateOrganizationUser(orgId, data);
                    if (!((_a = resp.data) === null || _a === void 0 ? void 0 : _a.id)) {
                        showError({
                            errorCode: String(resp.statusCode),
                            message: resp.code || "errorModifyingUser",
                            translationKey: resp.message || "errors:errorModifyingUser",
                        }, data);
                        return false;
                    }
                }
                else {
                    const data = Object.assign({}, validResult.data);
                    const resp = yield Services.organizations.adminCreateOrganizationUser(orgId, data);
                    if (!((_b = resp.data) === null || _b === void 0 ? void 0 : _b.id)) {
                        showError({
                            errorCode: String(resp.statusCode),
                            message: resp.code || "errorCreatingUser",
                            translationKey: resp.message || "errors:errorCreatingUser",
                        }, data);
                        return false;
                    }
                    else {
                        if ((_c = resp.data) === null || _c === void 0 ? void 0 : _c.merged) {
                            createNotification(getTranslation("general:users.adminInvited"));
                        }
                    }
                }
                return true;
            }
            else {
                return false;
            }
        });
    }
    function createFieldOptionsForUserTypeEdit(currentUserType, editUserIsFederated) {
        var _a;
        const options = [{ value: "USER", label: getTranslation(`enums:UserTypeEnum.USER`) }];
        if (currentUserType === "ADMIN" || currentUserType === "SUPERADMIN") {
            options.push({
                value: "ADMIN",
                label: getTranslation(`enums:UserTypeEnum.ADMIN`),
            });
        }
        if (currentUserType === "SUPERADMIN" && !editUserIsFederated) {
            options.push({
                value: "SUPERADMIN",
                label: getTranslation(`enums:UserTypeEnum.SUPERADMIN`),
            });
        }
        const editUserTypeField = {
            name: "organizationUserRole",
            label: "general:common.type",
            type: "radioButtonList",
            fieldset: true,
            initialValue: (_a = prevData === null || prevData === void 0 ? void 0 : prevData.organizationUserRole) !== null && _a !== void 0 ? _a : "USER",
            options,
            disabled: options.length <= 1 ||
                ((prevData === null || prevData === void 0 ? void 0 : prevData.organizationUserRole) === "SUPERADMIN" && currentUserType !== "SUPERADMIN"),
        };
        return editUserTypeField;
    }
}
function createOrganizationEditModal(submitButton, showError, canChangeFederations, myOrgFederationsFn) {
    const holder = document.createElement("div");
    let federationsNode = undefined;
    if (canChangeFederations && myOrgFederationsFn) {
        federationsNode = document.createElement("div");
        createFederationsTable({
            federationsNode,
            myOrgFederationsFn,
        });
    }
    const formFields = [
        [
            {
                name: "name",
                label: "general:common.organizationName",
                type: "textBox",
                initialValue: securityModule.currentOrganizationName || "",
            },
            {
                name: "mfaForAll",
                label: "auth:mfa.mfaForAllUsers",
                helpText: "auth:mfa.mfaForAllUsersInfo",
                type: "checkbox",
                padStart: true,
                initialValue: securityModule.organizationMfaForAllUsers,
            },
        ],
    ];
    if (federationsNode) {
        formFields.push({
            type: "title",
            label: "general:organizations.federations.title",
        });
        formFields.push({
            name: "federations",
            label: "general:organizations.federations.addFederation",
            type: "node",
            node: federationsNode,
        });
    }
    const formValidator = z.object({
        name: z.string().min(3),
        mfaForAll: z.boolean().optional(),
    });
    const tedivoForm = new TedivoForm({
        fields: formFields,
        onSubmit: () => undefined,
        formValidator,
        submitButton: submitButton,
        formProps: { autoFocusOnFirstInput: true },
    });
    translateTedivoForm({
        tedivoForm,
        getTranslation: getTranslation,
    });
    holder.appendChild(tedivoForm.form);
    return {
        node: holder,
        tedivoForm,
        submitFunction: submitPassedToEditDrawer,
    };
    function submitPassedToEditDrawer() {
        return __awaiter(this, void 0, void 0, function* () {
            const validResult = tedivoForm.doSubmitForm();
            if (validResult.success) {
                const resp = yield Services.accounts.changeOrganizationSelfDetails(validResult.data.name, validResult.data.mfaForAll || false);
                if (resp.statusCode !== 200) {
                    showError({
                        errorCode: String(resp.statusCode),
                        message: resp.code || "errorModifyingOrg",
                        translationKey: resp.message || "errors:errorModifyingOrg",
                    });
                    return false;
                }
                securityModule.updateOrganizationDetails(validResult.data.name, validResult.data.mfaForAll || false);
                return true;
            }
            else {
                return false;
            }
        });
    }
}
function createFederationsTable({ federationsNode, myOrgFederationsFn, }) {
    const divTable = new DivWithSpinner();
    divTable.className = "lids-table-mainHolder";
    federationsNode.appendChild(divTable);
    let federations = [];
    const smartTableConfig = {
        data: federations,
        defaultSortBy: "domain",
        className: "tvd-table tvd-table-smart-table tvd-table-sticky tvd-table-sticky-0",
        fields: [
            {
                name: "domain",
                label: getTranslation("general:domains.domain"),
                type: "string",
            },
            {
                name: "domain",
                label: getTranslation("general:organizations.federations.ownership"),
                type: "string",
                fixedWidth: "160px",
                className: (dta) => `centered ${(dta === null || dta === void 0 ? void 0 : dta.orgIdOwner) === securityModule.currentOrganizationId ? "text-success" : ""}`,
                valueFunction: (dta) => dta.orgIdOwner === securityModule.currentOrganizationId
                    ? getTranslation("general:common.yes")
                    : getTranslation("general:common.no"),
            },
            {
                name: "domainOwnershipVerified",
                type: "string",
                valueFunction: (dta) => dta.domainOwnershipVerified
                    ? getTranslation("general:common.yes")
                    : getTranslation("general:common.no"),
                className: (dta) => `centered ${(dta === null || dta === void 0 ? void 0 : dta.domainOwnershipVerified) ? "text-success" : ""}`,
                fixedWidth: "160px",
                label: getTranslation("general:common.verified"),
            },
            {
                name: "enabled",
                type: "string",
                className: "centered",
                fixedWidth: "100px",
                valueFunction: (dta) => dta.enabled ? getTranslation("general:common.yes") : getTranslation("general:common.no"),
                label: getTranslation("general:common.enabled"),
            },
            {
                name: "provider",
                type: "string",
                className: "centered",
                fixedWidth: "140px",
                valueFunction: (dta) => dta.domainOwnershipVerified && dta.enabled ? dta.provider || "-" : "-",
                label: getTranslation("general:organizations.federations.provider"),
            },
            {
                name: "id",
                className: "centered",
                label: getTranslation("general:common.actions"),
                type: "actions",
                iconVariant: "primary",
                icon: "three-dots-vertical",
                notSortable: true,
                fixedWidth: "60px",
                smallPadding: true,
                onClick: (dta, name, table, cmd) => {
                    switch (cmd) {
                        case "verifyDomain":
                            verifyDomainOwnership(dta);
                            break;
                        case "enable":
                            createEditEnableFederationDialog("general:organizations.federations.actions.enable", dta, true);
                            break;
                        case "disable":
                            disableFederation(dta);
                            break;
                    }
                },
                options: (d) => d.orgIdOwner !== securityModule.currentOrganizationId
                    ? []
                    : [
                        !d.domainOwnershipVerified
                            ? {
                                label: getTranslation("general:organizations.federations.actions.verifyDomain"),
                                value: "verifyDomain",
                                icon: "shield-check",
                            }
                            : undefined,
                        d.domainOwnershipVerified && !d.enabled
                            ? {
                                label: getTranslation("general:organizations.federations.actions.enable"),
                                value: "enable",
                                icon: "file-check",
                            }
                            : undefined,
                        d.domainOwnershipVerified && d.enabled
                            ? {
                                label: getTranslation("general:organizations.federations.actions.disable"),
                                value: "disable",
                                icon: "radioactive",
                                variant: "danger",
                            }
                            : undefined,
                    ].filter(Boolean),
            },
        ],
        settings: {
            filters: {
                textFieldEnabled: false,
            },
        },
        initialRows: 300,
        pkFunction: (dta) => `${dta.id}`,
    };
    const smartTable = new SmartTable().initialize(smartTableConfig);
    divTable.appendChild(smartTable);
    updateTableWithLatestData();
    return {
        divTable,
        smartTable,
    };
    function createFederationObjSchemaValidator() {
        const fedEditSchema = z.object({
            enabled: z.boolean(),
            domain: z.string().min(1),
            domainOwnershipVerificationToken: z.string().min(1),
            domainOwnershipVerified: z.boolean(),
            provider: z.nativeEnum(FederationProvidersEnum).optional(),
        });
        return fedEditSchema;
    }
    function createEditEnableFederationDialog(title, dta, selectProvider) {
        {
            const diag = new SlDialog();
            diag.label = getTranslation(title);
            diag.addEventListener("sl-after-hide", (event) => {
                if (event.target !== diag)
                    return;
                diag.remove();
            });
            const okButton = new SlButton();
            okButton.innerHTML = getTranslation(title);
            okButton.variant = "primary";
            okButton.type = "submit";
            okButton.slot = "footer";
            const domainNode = document.createElement("div");
            domainNode.innerHTML = `<strong>${dta.domain}</strong>`;
            const tForm = new TedivoForm({
                fields: [
                    {
                        type: "node",
                        name: "id",
                        node: domainNode,
                    },
                    {
                        name: "provider",
                        label: getTranslation("general:organizations.federations.provider"),
                        type: "select",
                        disabled: !selectProvider,
                        options: [
                            {
                                value: "",
                                label: getTranslation("general:common.none"),
                            },
                            {
                                value: FederationProvidersEnum.Microsoft,
                                label: "Microsoft",
                            },
                        ],
                        initialValue: dta.provider || "",
                    },
                ],
                onSubmit: (values) => __awaiter(this, void 0, void 0, function* () {
                    var _a, _b;
                    okButton.loading = true;
                    okButton.disabled = true;
                    yield Services.domainFederations.ssoEnableFederation(Object.assign(Object.assign({}, dta), { provider: values.provider, users: (_b = (_a = values === null || values === void 0 ? void 0 : values.users) === null || _a === void 0 ? void 0 : _a.replace(/\r/g, "").replace(/,/g, "\n")) === null || _b === void 0 ? void 0 : _b.split("\n").filter(Boolean) }));
                    updateTableWithLatestData();
                    diag.hide();
                }),
                formValidator: createFederationObjSchemaValidator(),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                submitButton: okButton,
                formProps: { autoFocusOnFirstInput: true },
                hiddenData: dta,
            });
            okButton.addEventListener("click", () => {
                tForm.doSubmitForm();
            });
            document.body.appendChild(diag);
            diag.appendChild(tForm.form);
            diag.appendChild(okButton);
            diag.show();
        }
    }
    function verifyDomainOwnership(dta) {
        const d = new IntegratedDialog({
            parentNode: document.body,
            buttonsAttrs: [
                {
                    value: "close",
                    variant: "primary",
                    label: getTranslation("general:common.ok"),
                    type: "button",
                },
                {
                    value: "verify",
                    variant: "success",
                    label: getTranslation("general:common.verify"),
                    type: "submit",
                },
            ],
            preventDefaultClose: true,
            removeDialogWhenHidden: true,
        });
        d.onButtonClicked = (value, btn) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (value !== "verify") {
                d.hide();
                return true;
            }
            btn.loading = true;
            const resp = yield Services.domainFederations.ssoCheckDnsTxt(dta.domain);
            btn.loading = false;
            d.hide();
            if (resp.statusCode !== 200) {
                createErrorNotification(getTranslation("errors:domainVerification.error"), 10000);
            }
            else {
                if (((_a = resp.data) === null || _a === void 0 ? void 0 : _a.found) === "OK") {
                    updateTableWithLatestData();
                    createNotification(getTranslation("general:organizations.federations.domainVerified", {
                        domain: dta.domain,
                    }), "success", "hand-thumbs-up", 10000);
                }
                else {
                    createErrorNotification(getTranslation(`errors:domainVerification.error_${(_b = resp.data) === null || _b === void 0 ? void 0 : _b.found}`, {
                        domain: dta.domain,
                        token: dta.domainOwnershipVerificationToken,
                    }), 10000);
                }
            }
            return true;
        });
        d.show(getTranslation("general:organizations.federations.actions.verifyDomain"), getTranslation("general:organizations.federations.actions.verifyDomainInfo", {
            token: dta.domainOwnershipVerificationToken,
            domain: dta.domain,
        }));
    }
    function disableFederation(dta) {
        const d = new IntegratedDialog({
            parentNode: document.body,
            buttonsAttrs: [
                {
                    value: "cancel",
                    variant: "primary",
                    label: getTranslation("general:common.cancel"),
                    type: "submit",
                },
                {
                    value: "disable",
                    variant: "danger",
                    label: getTranslation("general:common.disable"),
                    icon: "radioactive",
                    type: "button",
                },
            ],
            preventDefaultClose: true,
            removeDialogWhenHidden: true,
        });
        d.onButtonClicked = (action, btn) => __awaiter(this, void 0, void 0, function* () {
            if (action === "cancel") {
                d.hide();
                return true;
            }
            btn.loading = true;
            yield Services.domainFederations.ssoDisableFederation({
                domain: dta.domain,
            });
            btn.loading = false;
            d.hide();
            updateTableWithLatestData();
            return true;
        });
        d.show(getTranslation("general:organizations.federations.actions.disable"), getTranslation("general:organizations.federations.actions.disableInfo", {
            domain: dta.domain,
        }));
    }
    function updateTableWithLatestData() {
        divTable.setLoading(true);
        myOrgFederationsFn().then((feds) => {
            if (feds)
                smartTable.updateData(feds);
            federations = feds || [];
            divTable.setLoading(false);
        });
    }
}
